import type { MediaSource, MediaSourceType, MediaV2 } from '@/apis/queries/media-list.types';

export type MuteSettings = NonNullable<MediaV2['muteSettings']>;

// Audio detection / music muting applies to video-class media only. In the media-object
// vocabulary, MediaV2 `type` / legacy `mediaType` spell these 'VIDEO'/'REEL'.
export const AUDIO_MUTABLE_MEDIA_TYPES = ['VIDEO', 'REEL'] as const;

const UGC_SOURCE_TYPE: MediaSourceType = 'UGC';
const INSTAGRAM_SOURCES: ReadonlySet<MediaSource> = new Set(['INSTAGRAM', 'INSTAGRAM_STORY']);

export function isAudioMutableMediaType(type?: string | null): boolean {
  return (
    type != null && (AUDIO_MUTABLE_MEDIA_TYPES as readonly string[]).includes(type.toUpperCase())
  );
}

export function isInstagramUgcVideo(media: MediaV2): boolean {
  return (
    media.sourceType === UGC_SOURCE_TYPE &&
    INSTAGRAM_SOURCES.has(media.source) &&
    // A carousel reports type 'CAROUSEL'; baseMediaType carries its first-slide type, so a
    // video-first carousel is mutable while an image-first one is not.
    isAudioMutableMediaType(media.baseMediaType ?? media.type)
  );
}

export function isMutable(media: MediaV2): boolean {
  return (
    isInstagramUgcVideo(media) &&
    media.audioAnalysis?.hasMusic === true &&
    !media.muteSettings?.muteMusic &&
    !media.muteSettings?.muteAll
  );
}

// Either scope counts: the backend strips the whole track on speechless clips, and unmute
// clears every scope regardless of which one was applied.
export function isMuted(muteSettings?: MuteSettings | null): boolean {
  return muteSettings?.muteMusic === true || muteSettings?.muteAll === true;
}

export function isUnmutable(media: MediaV2): boolean {
  return isInstagramUgcVideo(media) && isMuted(media.muteSettings);
}
